import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";
import AnnouncementPopup from "../announcement/AnnouncementPopup";
import API_BASE_URL from "../config";
import {
  FaHome,
  FaSearch,
  FaSlidersH,
  FaSignOutAlt,
  FaUserCog,
  FaChevronDown,
  FaExchangeAlt,
} from "react-icons/fa";

/* ─────────────── tiny style injection ─────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .anav * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── top bar ── */
  .anav-topbar {
    background: #ffffff;
    border-bottom: 1px solid #eef0f4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    height: 58px;
    position: relative;
    z-index: 1100;
  }

  /* ── main navbar ── */
  .anav-bar {
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 24px;
    position: relative;
    z-index: 1050;
    transition: background-color 0.3s ease;
  }
  .anav-bar::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(255,255,255,0.08);
  }

  /* ── nav item ── */
  .anav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 14px;
    height: 46px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.18s ease;
    white-space: nowrap;
    user-select: none;
  }
  .anav-item:hover { color: #ffffff; }
  .anav-item::before {
    content: '';
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: rgba(255,255,255,0);
    border-radius: 2px 2px 0 0;
    transition: background 0.18s ease;
  }
  .anav-item:hover::before,
  .anav-item.active::before { background: rgba(255,255,255,0.7); }
  .anav-item .chevron {
    font-size: 10px;
    opacity: 0.7;
    margin-left: 1px;
    transition: transform 0.2s ease, opacity 0.2s;
  }
  .anav-item:hover .chevron { transform: rotate(180deg); opacity: 1; }

  /* ── dropdown panel (level 1) ── */
  .anav-drop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 190px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible;
    animation: dropIn 0.17s ease;
    z-index: 2000;
  }
  .anav-drop > a:first-child,
  .anav-drop > .anav-flyout-wrap:first-child > .anav-flyout-row { border-radius: 10px 10px 0 0; }
  .anav-drop > a:last-child,
  .anav-drop > .anav-flyout-wrap:last-child > .anav-flyout-row  { border-radius: 0 0 10px 10px; }
  .anav-drop > a:only-child { border-radius: 10px; }

  @keyframes dropIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes flyIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .anav-drop a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    transition: background 0.13s, color 0.13s;
    border-left: 3px solid transparent;
  }
  .anav-drop a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  /* ── level-1 flyout row ── */
  .anav-flyout-wrap { position: relative; }
  .anav-flyout-wrap > .anav-flyout-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap;
    user-select: none;
  }
  .anav-flyout-wrap:hover > .anav-flyout-row,
  .anav-flyout-wrap.open  > .anav-flyout-row {
    background: #f5f6ff; color: #080235; border-left-color: #5b5fc7;
  }
  .anav-flyout-wrap > .anav-flyout-row .fly-arrow { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  /* ── level-2 flyout panel ── */
  .anav-flyout-panel {
    position: absolute;
    top: -1px; left: 100%;
    min-width: 200px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible;
    animation: flyIn 0.15s ease;
    z-index: 3000;
  }
  .anav-flyout-panel > a:first-child,
  .anav-flyout-panel > .anav-flyout-wrap2:first-child > .anav-flyout-row2 { border-radius: 10px 10px 0 0; }
  .anav-flyout-panel > a:last-child,
  .anav-flyout-panel > .anav-flyout-wrap2:last-child  > .anav-flyout-row2 { border-radius: 0 0 10px 10px; }
  .anav-flyout-panel > a:only-child { border-radius: 10px; }
  .anav-flyout-panel > a {
    display: block;
    padding: 9px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .anav-flyout-panel > a:hover { background: #f5f6ff; color: #080235; border-left-color: #5b5fc7; }

  /* ── level-2 flyout row (grandchildren trigger) ── */
  .anav-flyout-wrap2 { position: relative; }
  .anav-flyout-wrap2 > .anav-flyout-row2 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap;
    user-select: none;
  }
  .anav-flyout-wrap2:hover > .anav-flyout-row2,
  .anav-flyout-wrap2.open  > .anav-flyout-row2 {
    background: #f5f6ff; color: #080235; border-left-color: #5b5fc7;
  }
  .anav-flyout-wrap2 > .anav-flyout-row2 .fly-arrow2 { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  /* ── level-3 flyout panel ── */
  .anav-flyout-panel2 {
    position: absolute;
    top: -1px; left: 100%;
    min-width: 160px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: hidden;
    animation: flyIn 0.15s ease;
    z-index: 4000;
  }
  .anav-flyout-panel2 a {
    display: block;
    padding: 9px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .anav-flyout-panel2 a:hover { background: #f5f6ff; color: #080235; border-left-color: #5b5fc7; }

  .anav-drop-divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

  /* ── search ── */
  .anav-search {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 22px;
    padding: 0 14px;
    height: 32px;
    gap: 8px;
    transition: background 0.2s, border 0.2s, width 0.3s ease;
    width: 180px;
    margin-left: 12px;
  }
  .anav-search:focus-within {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.4);
    width: 220px;
  }
  .anav-search input { background: transparent; border: none; outline: none; color: #fff; font-size: 13px; width: 100%; }
  .anav-search input::placeholder { color: rgba(255,255,255,0.55); }
  .anav-search svg { color: rgba(255,255,255,0.6); flex-shrink: 0; }

  /* ── profile card ── */
  .anav-profile-btn {
    display: flex; align-items: center; gap: 10px;
    cursor: pointer; padding: 6px 10px; border-radius: 10px; transition: background 0.15s;
  }
  .anav-profile-btn:hover { background: #f4f5fb; }
  .anav-avatar {
    width: 36px; height: 36px; border-radius: 50%; border: 2px solid #e8eaf0;
    object-fit: cover; background: #e8eaf0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px; color: #374151;
  }
  .anav-profile-name { font-size: 14px; font-weight: 600; color: #1a1d2e; line-height: 1.2; }
  .anav-profile-role { font-size: 11px; font-weight: 500; color: #8b90a7; }

  /* ── profile dropdown ── */
  .anav-profile-drop {
    position: absolute; top: calc(100% + 6px); right: 0;
    min-width: 220px; background: #fff; border: 1px solid #e8eaf0;
    border-radius: 12px; box-shadow: 0 16px 40px rgba(8,2,53,0.13);
    overflow: hidden; animation: dropIn 0.17s ease; z-index: 3000;
  }
  .anav-profile-header {
    padding: 14px 16px 10px;
    background: linear-gradient(135deg, #f8f9ff 0%, #eef0fb 100%);
    border-bottom: 1px solid #e8eaf0;
  }
  .anav-profile-header .name { font-size: 14px; font-weight: 700; color: #1a1d2e; }
  .anav-profile-header .role-badge {
    display: inline-block; margin-top: 4px; padding: 2px 10px;
    font-size: 10.5px; font-weight: 600; border-radius: 20px;
    background: #080235; color: #fff; letter-spacing: 0.4px; text-transform: uppercase;
  }
  .anav-profile-drop .menu-item {
    display: flex; align-items: center; gap: 10px; padding: 10px 16px;
    font-size: 13px; font-weight: 500; color: #2d3142;
    cursor: pointer; text-decoration: none; transition: background 0.13s;
  }
  .anav-profile-drop .menu-item:hover { background: #f5f6ff; }
  .anav-profile-drop .menu-item.danger { color: #dc2626; }
  .anav-profile-drop .menu-item.danger:hover { background: #fff5f5; }
  .anav-profile-drop .divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

  /* ── settings btn ── */
  .anav-settings-btn {
    display: flex; align-items: center; gap: 6px; padding: 6px 14px;
    border-radius: 8px; font-size: 15px; font-weight: 600; color: #444b6b;
    cursor: pointer; transition: background 0.15s, color 0.15s;
  }
  .anav-settings-btn:hover { background: #f0f1f8; color: #080235; }

  /* ── home btn ── */
  .anav-home-btn {
    display: flex; align-items: center; padding: 7px 10px;
    border-radius: 8px; color: #6b7394; transition: background 0.15s, color 0.15s;
  }
  .anav-home-btn:hover { background: #f0f1f8; color: #080235; }

  /* ── role switch pills ── */
  .role-pill {
    display: inline-block; padding: 4px 12px; font-size: 11.5px; font-weight: 600;
    border-radius: 20px; background: #f0f1f8; color: #3a4060;
    cursor: pointer; margin: 2px 0; transition: background 0.15s, color 0.15s; text-decoration: none;
  }
  .role-pill:hover { background: #080235; color: #fff; }

  /* ── spinner ── */
  @keyframes spin { to { transform: rotate(360deg); } }
  .anav-spinner {
    width: 18px; height: 18px; border: 2px solid #e5e7eb;
    border-top-color: #6b7394; border-radius: 50%; animation: spin 0.7s linear infinite;
  }
`;

/* ─────────────── component ─────────────── */
const AdminNavbar = () => {
  const navigate = useNavigate();
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [photoObjectUrl, setPhotoObjectUrl] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const topbarRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [profile, setProfile] = useState({ username: "Admin", role: "", roles: [], photo: null });
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [permissions, setPermissions] = useState({});
  const [openFlyout, setOpenFlyout] = useState(null); // level-2
  const [openFlyout2, setOpenFlyout2] = useState(null); // level-3
  const employeeId = localStorage.getItem("employeeId");
  const activeRole = localStorage.getItem("activeRole") || profile.role;

  /* ── permissions ── */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const employeeId = localStorage.getItem("employeeId");
        const res = await fetch(`${API_BASE_URL}/api/permissions/${employeeId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setPermissions(data.pages || {});
      } catch (err) { console.error("Permission fetch error:", err); }
    })();
  }, []);

  /* ── timer helpers ── */
  const open = (key) => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); setOpenDropdown(key); };
  const close = () => { closeTimerRef.current = setTimeout(() => { setOpenDropdown(null); setOpenFlyout(null); setOpenFlyout2(null); }, 120); };
  const keepOpen = () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); };

  /* ── auth ── */
  useEffect(() => {
    localStorage.setItem("activeRole", "ROLE_ADMIN");
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); }
  }, [navigate]);

  /* ── appearance ── */
  useEffect(() => {
    const token = localStorage.getItem("token"); const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, []);

  /* ── photo ── */
  useEffect(() => {
    const token = localStorage.getItem("token"); if (!token) return;
    let mounted = true, objUrl = null;
    setPhotoLoading(true); setPhotoError(false);
    fetch(`${API_BASE_URL}/api/employees/me/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => {
        if (!mounted) return;
        if (blob?.size > 0) { objUrl = URL.createObjectURL(blob); setPhotoObjectUrl(objUrl); setProfile(p => ({ ...p, photo: objUrl })); }
        else setPhotoError(true);
      })
      .catch(() => { if (mounted) setPhotoError(true); })
      .finally(() => { if (mounted) setPhotoLoading(false); });
    return () => { mounted = false; if (objUrl) URL.revokeObjectURL(objUrl); setPhotoObjectUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); };
  }, []);

  /* ── company logo ── */
  useEffect(() => {
    const token = localStorage.getItem("token"); if (!token) return;
    fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
      .then(r => r.json()).then(d => { if (Array.isArray(d) && d[0]?.logoPath) setCompanyLogo(`${API_BASE_URL}/${d[0].logoPath}`); }).catch(() => { });
  }, []);

  /* ── profile ── */
  useEffect(() => {
    const token = localStorage.getItem("token"); if (!token) return;
    fetch(`${API_BASE_URL}/api/users/admins`, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (d?.length > 0) {
          const a = d[0];
          setProfile(p => ({ ...p, username: a.name || p.username, email: a.email, role: localStorage.getItem("activeRole") || (Array.isArray(a.roles) ? a.roles[0] : p.role), roles: a.roles || p.roles, status: a.status }));
        }
      }).catch(() => { });
  }, []);

  /* ── outside click ── */
  useEffect(() => {
    const h = (e) => { if (topbarRef.current && !topbarRef.current.contains(e.target)) setOpenDropdown(null); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  /* ── avatar ── */
  const Avatar = () => {
    if (photoLoading) return <div className="anav-avatar"><div className="anav-spinner" /></div>;
    if (!photoError && profile.photo) return <img src={profile.photo} alt="avatar" className="anav-avatar" onError={() => { setPhotoError(true); setProfile(p => ({ ...p, photo: null })); }} />;
    return <div className="anav-avatar">{(profile.username || "U").charAt(0).toUpperCase()}</div>;
  };

  /* ─────────── nav items ─────────── */
  const navItems = [
    // {
    //   key: "masters", label: "Masters", landing: "/masterslanding",
    //   links: [
    //     { label: "Departments", href: "/department" },
    //     { label: "Roles", href: "/role" },
    //     { label: "Designations", href: "/designation" },
    //     { label: "Document", href: "/document" },
    //     { label: "Leave Types", href: "/leavetype" },
    //     { label: "Team", href: "/team" },
    //     { label: "Customer", href: "/customermgmt" },
    //     { label: "Project", href: "/project" },
    //     { label: "Holidays", href: "/holiday" },
    //     { label: "Terms & Conditions", href: "/termconditions" },
    //   ],
    // },
    {
      key: "setup", label: "Setup", landing: "/setuplanding",
      links: [
        {
          label: "Departments",
          href: "/department",
          children: [
            { label: "Departments", href: "/department" },

          ],
        },
        {
          label: "Roles",
          href: "/role",
          children: [
            { label: "Roles", href: "/role", },
            // { label: "New Customer", href: "/addcustomer" },
          ],
        },
        {
          label: "Designations",
          href: "/designation",
          children: [
            { label: "Designations", href: "/designation", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Documents",
          href: "/document",
          children: [
            { label: "Documents", href: "/document", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Leave Types",
          href: "/leavetype",
          children: [
            { label: "Leave Types", href: "/leavetype", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Teams",
          href: "/team",
          children: [
            { label: "Teams", href: "/team", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Holidays",
          href: "/holiday",
          children: [
            { label: "Holidays", href: "/holiday", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Terms",
          href: "/termconditions",
          children: [
            { label: "Terms", href: "/termconditions", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Permissions",
          href: "/permissions",
          children: [
            { label: "Permissions", href: "/permissions", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        {
          label: "Integrations",
          href: "/integrationlanding",
          children: [
            { label: "TeamWork", href: "/teamworkint", },
            { label: "Jira", href: "/jira-tempo-integration" },
          ],
        },
         {
          label: "Deletion Registry",
          href: "/deleted-entries",
          children: [
            { label: "Deletion Registry", href: "/deleted-entries", },
            // { label: "New Project", href: "/addproject" },
          ],
        },
      ],
    },
    {
      key: "list", label: "List", landing: "/listlanding",
      links: [
        {
          label: "Employee",
          children: [
            { label: "Employees", href: "/allemployee", grandchildren: [{ label: "New", href: "/addemployee" },] },
            { label: "Documents", href: "/viewalldocs" }, { label: "Allocation", href: "/allocation" }, { label: "Evaluation", href: "/evaluation" },
          ],
        },
        {
          label: "Customer",
          children: [
            { label: "Customers", href: "/allcustomers", grandchildren: [{ label: "New", href: "/addcustomer" },] },
            { label: "Contacts", href: "/allcontacts", grandchildren: [{ label: "New", href: "/addcontact" },] },
          ],
        },
        {
          label: "Project",
          children: [
            { label: "Projects", href: "/allprojects", grandchildren: [{ label: "New", href: "/addproject" },] },
            // { label: "New Project", href: "/addproject" },
          ],
        },
        // {
        //   label: "Vendor",
        //   children: [
        //     { label: "Vendors", href: "/vendors", grandchildren: [{ label: "New", href: "/addvendor" },] },
        //     // { label: "New Project", href: "/addproject" },
        //   ],
        // },

      ],
    },
    {
      key: "assets",
      label: "Asset Management",
      landing: "/admin-assets",
      links: [
        {
          label: "Asset Register",
          children: [
            { label: "All Assets", href: "/allassets", grandchildren: [{ label: "New", href: "/addasset" }] },
            { label: "Allocations", href: "/asset-allocations", grandchildren: [{ label: "New", href: "/newallocation" }] },
            { label: "Transfers", href: "/asset-transfers", grandchildren: [{ label: "New", href: "/newtransfer" }] },
            { label: "Maintenance", href: "/asset-maintenance", grandchildren: [{ label: "New", href: "/newmaintenance" }] },
            { label: "Audit", href: "/asset-audit", grandchildren: [{ label: "New", href: "/newauditrecord" }] },
            { label: "Field Master", href: "/asset-field-master" },
            { label: "Reports", href: "/asset-reports" },
            { label: "Vendors", href: "/vendors", grandchildren: [{ label: "New", href: "/addvendor" }] },
          ],
        },
      ],
    },
    // {
    //   key: "employees", label: "Employee", landing: "/employeelanding",
    //   links: [
    //     { label: "New Employee", href: "/addemployee" },
    //     { label: "Employee List", href: "/allemployee" },
    //     { label: "Documents", href: "/viewalldocs" },
    //     { label: "Team Management", href: "/adminmanageteams" },
    //     { label: "Allocation", href: "/allocation" },
    //     { label: "Evaluation", href: "/evaluation" },
    //   ],
    // },
    { key: "tasks", label: "Tasks", landing: "/admintasklanding", links: [{ label: "Review Task", href: "/adminviewtasks" },{ label: "Sprint", href: "/sprints" }] },
    { key: "timesheets", label: "Timesheets", landing: "/admintimesheetlanding", links: [{ label: "View Timesheets", href: "/admintimesheetgrid" }] },
    // { key: "integrationss", label: "Integrations", landing: "/integrationlanding", links: [{ label: "TeamWork", href: "/teamworkint" }] },
    { key: "leave management", label: "Leave Management", landing: "/adminleavemanagementlanding", links: [{ label: "Leave Management", href: "/leavemanagement" }] },
    // { key: "permissions", label: "Permissions", landing: "/permissionslanding", links: [{ label: "Permission Management", href: "/permissions" }] },

     {
      key: "expenses", label: "Expenses", landing: "/admin-expenses",
      links: [
        { label: "Expense Dashboard", href: "/admin-expenses" },
      ],
    },


    {
      key: "alerts", label: "Alerts", landing: "/alertslanding",
      links: [
        ...(permissions?.announcement
          ? [{ label: "Announcement", onClick: (e) => { e.preventDefault(); navigate("/announcement", { state: { employeeId } }); } }]
          : []),
      ],
    },
   
    /* ══ 3-level List ══
       List ▾
         Customer ›
           Customers ›  →  New  /  Search
           New Customer
         Project ›
           Projects ›   →  New  /  Search
           New Project
    */

  ];

  /* ─────────── render helpers ─────────── */

  /* Level-3 panel */
  const Level3Panel = ({ grandchildren, flyKey2 }) =>
    openFlyout2 === flyKey2 ? (
      <div className="anav-flyout-panel2">
        {grandchildren.map((gc, k) => (
          <a key={k} href={gc.href}
            onClick={e => { e.preventDefault(); setOpenDropdown(null); setOpenFlyout(null); setOpenFlyout2(null); navigate(gc.href); }}>
            {gc.label}
          </a>
        ))}
      </div>
    ) : null;

  /* Level-2 panel */
  const Level2Panel = ({ children, itemKey, lnkIdx }) =>
    openFlyout === `${itemKey}-${lnkIdx}` ? (
      <div className="anav-flyout-panel" onMouseEnter={keepOpen}>
        {children.map((child, j) => {
          const flyKey2 = `${itemKey}-${lnkIdx}-${j}`;
          if (child.grandchildren) {
            return (
              <div key={j}
                className={`anav-flyout-wrap2${openFlyout2 === flyKey2 ? " open" : ""}`}
                onMouseEnter={() => { keepOpen(); setOpenFlyout2(flyKey2); }}
                onMouseLeave={() => setOpenFlyout2(null)}
              >

                <div className="anav-flyout-row2"
                  onClick={() => {
                    if (child.href) {
                      setOpenDropdown(null);
                      setOpenFlyout(null);
                      setOpenFlyout2(null);
                      navigate(child.href);
                    }
                  }}
                >
                  {child.label}
                  <span className="fly-arrow2">›</span>
                </div>
                <Level3Panel grandchildren={child.grandchildren} flyKey2={flyKey2} />
              </div>
            );
          }
          return (
            <a key={j} href={child.href}
              onClick={e => { e.preventDefault(); setOpenDropdown(null); setOpenFlyout(null); navigate(child.href); }}>
              {child.label}
            </a>
          );
        })}
      </div>
    ) : null;

  /* Level-1 link renderer */
  const renderLinks = (item) =>
    item.links.map((lnk, i) => {
      if (lnk.divider) return <div key={i} className="anav-drop-divider" />;
      if (lnk.isHeader) return (
        <div key={i} style={{ padding: "7px 16px 3px", fontSize: 10.5, fontWeight: 700, color: "#9ea3bb", textTransform: "uppercase", letterSpacing: "0.6px" }}>
          {lnk.label}
        </div>
      );

      if (lnk.children) {
        const flyKey = `${item.key}-${i}`;
        return (
          <div key={i}
            className={`anav-flyout-wrap${openFlyout === flyKey ? " open" : ""}`}
            onMouseEnter={() => { keepOpen(); setOpenFlyout(flyKey); setOpenFlyout2(null); }}
            onMouseLeave={() => { setOpenFlyout(null); setOpenFlyout2(null); }}
          >
            <div
              className="anav-flyout-row"
              onClick={() => {
                if (lnk.href) {
                  setOpenDropdown(null);
                  setOpenFlyout(null);
                  setOpenFlyout2(null);
                  navigate(lnk.href);
                }
              }}
            >
              {lnk.label}
              <span className="fly-arrow">›</span>
            </div>
            <Level2Panel children={lnk.children} itemKey={item.key} lnkIdx={i} />
          </div>
        );
      }

      return (
        <a key={i} href={lnk.href}
          onClick={lnk.onClick ?? (e => { e.preventDefault(); setOpenDropdown(null); navigate(lnk.href); })}>
          {lnk.label}
        </a>
      );
    });

  /* ─────────── JSX ─────────── */
  return (
    <>
      <AnnouncementPopup />
      <div className="anav">
        <style>{STYLES}</style>

        {/* ════════ TOP BAR ════════ */}
        <div className="anav-topbar" ref={topbarRef}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img src={logo} style={{ height: 39 }} alt="logo" />
            {companyLogo && (
              <img src={companyLogo} alt="company"
                style={{ height: 37, width: "auto", objectFit: "contain", borderRadius: 4, borderLeft: "2px solid #eef0f4", paddingLeft: 16 }} />
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <a href="/admindashboard" className="anav-home-btn" title="Home"><FaHome size={20} /></a>

            <div style={{ position: "relative" }} onClick={() => setOpenDropdown(prev => prev === "settings" ? null : "settings")}>
              <div className="anav-settings-btn">
                <FaSlidersH size={15} /> Settings
                <FaChevronDown size={10} style={{ opacity: 0.6, marginLeft: 2, transition: "transform 0.2s", transform: openDropdown === "settings" ? "rotate(180deg)" : "none" }} />
              </div>
              {openDropdown === "settings" && (
                <div className="anav-drop" style={{ right: 0, left: "auto" }}>
                  <a href="/settingpage" onClick={e => { e.preventDefault(); navigate("/settingpage"); }}>Company Settings</a>
                  <a href="/preference" onClick={e => { e.preventDefault(); navigate("/preference"); }}>Preferences</a>
                </div>
              )}
            </div>

            <div style={{ width: 1, height: 32, background: "#eef0f4", margin: "0 8px" }} />

            <div style={{ position: "relative" }} onClick={() => setOpenDropdown(prev => prev === "profile" ? null : "profile")}>
              <div className="anav-profile-btn">
                <Avatar />
                <div>
                  <div className="anav-profile-name">{profile.username}</div>
                  <div className="anav-profile-role">{(localStorage.getItem("activeRole") || profile.role)?.replace("ROLE_", "")}</div>
                </div>
                <FaChevronDown size={10} style={{ opacity: 0.5, marginLeft: 4, transition: "transform 0.2s", transform: openDropdown === "profile" ? "rotate(180deg)" : "none" }} />
              </div>

              {openDropdown === "profile" && (
                <div className="anav-profile-drop">
                  <div className="anav-profile-header">
                    <div className="name">{profile.username}</div>
                    {profile.email && <div style={{ fontSize: 11.5, color: "#8b90a7", marginTop: 2 }}>{profile.email}</div>}
                    <span className="role-badge">{(localStorage.getItem("activeRole") || profile.role)?.replace("ROLE_", "")}</span>
                  </div>

                  <a className="menu-item" href="/adminprofile">
                    <FaUserCog size={14} style={{ opacity: 0.7 }} /> Profile Settings
                  </a>

                  {profile.roles?.length > 1 && (
                    <>
                      <div className="divider" />
                      <div style={{ padding: "8px 16px" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: "#9ea3bb", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>
                          <FaExchangeAlt style={{ marginRight: 5, opacity: 0.7 }} />Switch Role
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {profile.roles.filter(r => r !== activeRole).map((role, i) => {
                            let path = "/";
                            if (role === "ROLE_MANAGER") path = "/managerdashboard";
                            if (role === "ROLE_ADMIN") path = "/admindashboard";
                            if (role === "ROLE_USER") path = "/empdashboard";
                            return (
                              <a key={i} className="role-pill" href={path}
                                onClick={e => { e.preventDefault(); localStorage.setItem("activeRole", role); setProfile(p => ({ ...p, role })); navigate(path); }}>
                                {role.replace("ROLE_", "")}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="divider" />
                  <div className="menu-item danger" onClick={() => { sessionStorage.clear(); localStorage.clear(); navigate("/"); }}>
                    <FaSignOutAlt size={13} /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ════════ MAIN NAV BAR ════════ */}
        <div className="anav-bar" style={{ backgroundColor: navbarColor }}>
          {(() => {
            const visibleItems = navItems.filter(item => item.links.length > 0);
            const count = visibleItems.length;
            const itemStyle = count > 10
              ? { fontSize: "12px", padding: "0 8px" }
              : count > 8
              ? { fontSize: "13px", padding: "0 10px" }
              : {};
            return (
              <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 2 }}>
                {visibleItems.map(item => (
                  <div key={item.key} style={{ position: "relative" }}
                    onMouseEnter={() => open(item.key)}
                    onMouseLeave={close}
                  >
                    <div
                      className={`anav-item${openDropdown === item.key ? " active" : ""}`}
                      style={itemStyle}
                      onClick={() => navigate(item.landing)}
                    >
                      {item.label}
                      <FaChevronDown className="chevron" />
                    </div>
                    {openDropdown === item.key && (
                      <div className="anav-drop">
                        {renderLinks(item)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}


          <div className="anav-search">
            <FaSearch size={12} />
            <input type="text" placeholder="Search…" value={searchVal} onChange={e => setSearchVal(e.target.value)} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminNavbar;
